/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import {
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActionArea,
  CardActions,
  CardHeader,
  Avatar,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import MapIcon from "@mui/icons-material/Map";
import CloseIcon from "@mui/icons-material/Close";
import useGetDetailledObservation from "@/hooks/useGetDetailledObservation";
import useAuthenticatedQuery from "@/hooks/useAuthenticatedQuery";
import ObservationDetailsDialog from "./observationDetailsDialog";
import { useSession } from "@/hooks/useSession";
import useDeleteData from "@/hooks/deleteHooks/useDeleteData";
import DeleteModal from "@/components/deleteModal";
import Toast from "@/components/toast";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import { styled, useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  APIProvider,
  AdvancedMarker,
  Map as GoogleMap,
  useMap,
  Pin,
  InfoWindow,
} from "@vis.gl/react-google-maps";
import { convertUTCToLocal, getOffsetPosition } from "@/lib/utils/dateUtils";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { Circle } from "@/components/circle";
import { useTranslation } from "react-i18next";
import PageHeader from "@/components/PageHeader";

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  transition: "box-shadow 0.2s ease-in-out",
  "&:hover": {
    boxShadow: theme.shadows[6],
  },
  position: "relative",
  backgroundColor: theme.palette.background.paper,
}));

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(255, 255, 255, 0.8)",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 10,
  borderRadius: theme.shape.borderRadius,
}));

const MapContainerStyle = {
  width: "100%",
  height: "600px",
};

const londonCenter = {
  lat: 51.5074,
  lng: -0.1278,
};

function a11yProps(index) {
  return {
    id: `tab-${index}`,
    "aria-controls": `tabpanel-${index}`,
  };
}

// TabPanel Component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// ObservationCard Component
function ObservationCard({
  observation,
  loading,
  onDetailClick,
  onDeleteClick,
  t,
}) {
  const { session } = useSession();
  const theme = useTheme();

  if (!observation?.history_steps?.length) {
    return null;
  }

  const lastStep =
    observation.history_steps[observation.history_steps.length - 1];
  const rawStatus = lastStep?.status;
  const rawStatusDate = lastStep?.created_at;

  const latest = rawStatus
    ? rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase()
    : t("myObservations.unknownStatus");

  return (
    <StyledCard
      elevation={2}
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          elevation: 8,
          borderColor: theme.palette.primary.main,
          transform: "translateY(-2px)",
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        },
      }}
    >
      {loading && (
        <LoadingOverlay>
          <CircularProgress size={32} color="primary" />
          <Typography variant="body2" color="text.secondary" mt={1}>
            {t("myObservations.loadingDetails")}
          </Typography>
        </LoadingOverlay>
      )}

      <CardActionArea
        onClick={() => onDetailClick(observation.id)}
        sx={{
          flexGrow: 1,
          borderRadius: 3,
        }}
      >
        <CardHeader
          avatar={
            <Avatar
              src={observation.author?.avatar_url}
              alt={observation.author?.username}
              sx={{
                width: 48,
                height: 48,
                border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            />
          }
          title={
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: "1.1rem",
              }}
            >
              {observation.workflow?.name || t("myObservations.noWorkflow")}
            </Typography>
          }
          subheader={
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              {convertUTCToLocal(observation.created_at)}
            </Typography>
          }
          sx={{ pb: 1 }}
        />

        <CardContent sx={{ pt: 0, pb: 3 }}>
          <Stack spacing={2}>
            {/* Site and Priority Info */}
            <Box
              sx={{
                bgcolor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 2,
                p: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    {t("myObservations.siteLabel")}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {observation.site?.name || t("myObservations.unknownSite")}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    {t("myObservations.priorityLabel")}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {observation.observation_priority?.name ||
                      t("myObservations.unknownPriority")}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Description */}
            {observation.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.5,
                  fontStyle: "italic",
                }}
              >
                &ldquo;{observation.description}&rdquo;
              </Typography>
            )}

            {/* Timeline */}
            <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.5) }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                {t("myObservations.statusAtLabel", { status: latest })}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                {convertUTCToLocal(rawStatusDate)}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>

      {session?.user_type === "ORG_ADMIN" && (
        <CardActions
          sx={{
            justifyContent: "flex-end",
            px: 2,
            py: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            onClick={() => onDeleteClick(observation.id)}
            sx={{
              color: theme.palette.error.main,
              "&:hover": {
                bgcolor: alpha(theme.palette.error.main, 0.1),
              },
            }}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </CardActions>
      )}
    </StyledCard>
  );
}

// Priority Color Mapping - uses priority code (P1, P2, P3) which is language-independent
const priorityColorMap = {
  "P1": "#FF0000", // Red for High
  "P2": "#FFA500", // Orange for Medium
  "P3": "#008000", // Green for Low
};

// Helper function to extract priority code from priority name
function getPriorityColor(priorityName) {
  if (!priorityName) return "#FBBC04"; // Default yellow
  
  // Extract P1, P2, or P3 from the priority name (e.g., "P1 - High", "P1 - Yüksek", etc.)
  const match = priorityName.match(/^(P\d+)/i);
  if (match) {
    const code = match[1].toUpperCase();
    return priorityColorMap[code] || "#FBBC04";
  }
  
  return "#FBBC04"; // Default yellow
}

// PoiMarkers Component
function PoiMarkers({ pois, observations, onMarkerClick, t }) {
  const map = useMap();
  const [markers, setMarkers] = useState({});
  const [listeners, setListeners] = useState({});
  const clusterer = useRef(null);
  const [circleCenter, setCircleCenter] = useState(null);
  const [infoWindowPosition, setInfoWindowPosition] = useState(null);
  const [infoWindowObs, setInfoWindowObs] = useState([]);

  const obsByLocation = useMemo(() => {
    const grouped = {};
    observations.forEach((obs) => {
      if (obs.site?.latitude && obs.site?.longitude) {
        const key = `${obs.site.latitude},${obs.site.longitude}`;
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(obs);
      }
    });
    return grouped;
  }, [observations]);

  const handleMarkerClick = useCallback(
    (obsId, lat, lng, obsAtLocation) => {
      map.panTo({ lat, lng });
      setCircleCenter({ lat, lng });
      if (obsAtLocation.length > 1) {
        setInfoWindowPosition({ lat, lng });
        setInfoWindowObs(obsAtLocation);
      } else {
        setInfoWindowPosition(null);
        setInfoWindowObs([]);
        onMarkerClick(obsId);
      }
    },
    [map, onMarkerClick]
  );

  const handleInfoWindowClose = () => {
    setInfoWindowPosition(null);
    setInfoWindowObs([]);
  };

  useEffect(() => {
    if (!map || clusterer.current) return;
    clusterer.current = new MarkerClusterer({ map });
  }, [map]);

  useEffect(() => {
    clusterer.current?.clearMarkers();
    clusterer.current?.addMarkers(
      Object.values(markers).filter((marker) => marker)
    );
  }, [markers]);

  const setMarkerRef = (marker, key) => {
    if (!key) {
      return;
    }
    if (marker && markers[key]) return;
    if (!marker && !markers[key]) return;

    setMarkers((prev) => {
      const newMarkers = { ...prev };
      if (marker) {
        newMarkers[key] = marker;
      } else {
        delete newMarkers[key];
      }
      return newMarkers;
    });

    setListeners((prev) => {
      const newListeners = { ...prev };
      if (marker) {
        const [, id, lat, lng] = key.split("|");
        const locKey = `${lat},${lng}`;
        const obsAtLocation = obsByLocation[locKey] || [];
        const listener = window.google.maps.event.addListener(
          marker,
          "gmp-click",
          () =>
            handleMarkerClick(
              id,
              parseFloat(lat),
              parseFloat(lng),
              obsAtLocation
            )
        );
        newListeners[key] = listener;
      } else {
        if (newListeners[key]) {
          window.google.maps.event.removeListener(newListeners[key]);
        }
        delete newListeners[key];
      }
      return newListeners;
    });
  };

  useEffect(() => {
    return () => {
      Object.values(listeners).forEach((listener) => {
        if (listener) {
          window.google.maps.event.removeListener(listener);
        }
      });
      clusterer.current?.clearMarkers();
    };
  }, []);

  return (
    <>
      <Circle
        radius={800}
        center={circleCenter}
        strokeColor="#0c4cb3"
        strokeOpacity={1}
        strokeWeight={3}
        fillColor="#3b82f6"
        fillOpacity={0.3}
      />
      {pois.map((poi) => {
        const obs = observations.find((o) => o.id === poi.key);
        if (!obs || !poi.key || !poi.location?.lat || !poi.location?.lng) {
          return null;
        }
        const locKey = `${poi.location.lat},${poi.location.lng}`;
        const obsAtLocation = obsByLocation[locKey] || [obs];
        const index = obsAtLocation.findIndex((o) => o.id === poi.key);
        const offsetPosition = getOffsetPosition(
          poi.location,
          index,
          obsAtLocation.length
        );
        const priorityColor = getPriorityColor(obs.observation_priority?.name);
        return (
          <AdvancedMarker
            key={`obs|${poi.key}|${poi.location.lat}|${poi.location.lng}`}
            position={offsetPosition}
            ref={(marker) =>
              setMarkerRef(
                marker,
                `obs|${poi.key}|${poi.location.lat}|${poi.location.lng}`
              )
            }
          >
            <Pin
              background={priorityColor}
              glyphColor="#000"
              borderColor="#000"
            />
          </AdvancedMarker>
        );
      })}
      {infoWindowPosition && infoWindowObs.length > 0 && (
        <InfoWindow
          position={infoWindowPosition}
          onCloseClick={handleInfoWindowClose}
        >
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {t("myObservations.infoWindowTitle")}
            </Typography>
            {infoWindowObs.map((obs) => (
              <Typography
                key={obs.id}
                variant="body2"
                sx={{
                  cursor: "pointer",
                  color: "blue",
                  textDecoration: "underline",
                }}
                onClick={() =>
                  handleMarkerClick(
                    obs.id,
                    obs.site.latitude,
                    obs.site.longitude,
                    [obs]
                  )
                }
              >
                {obs.author.first_name} {obs.author.last_name} -{" "}
                {obs.observation_priority?.name ||
                  t("myObservations.unknownPriority")}
              </Typography>
            ))}
          </Box>
        </InfoWindow>
      )}
    </>
  );
}

// ObservationsMap Component
function ObservationsMap({
  observations,
  markerLocations,
  mapsLoaded,
  onMarkerClick,
  t,
}) {
  const map = useMap();

  useEffect(() => {
    if (
      !map ||
      !mapsLoaded ||
      !window.google?.maps ||
      typeof window.google.maps.LatLngBounds !== "function" ||
      !markerLocations.length
    ) {
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    markerLocations.forEach((poi) => {
      if (
        Number.isFinite(poi.location.lat) &&
        Number.isFinite(poi.location.lng)
      ) {
        bounds.extend({
          lat: poi.location.lat,
          lng: poi.location.lng,
        });
      }
    });

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds);
    } else {
      map.setCenter(londonCenter);
      map.setZoom(14);
    }
  }, [map, markerLocations, mapsLoaded]);

  return (
    <GoogleMap
      style={MapContainerStyle}
      defaultCenter={londonCenter}
      defaultZoom={14}
      mapId="YOUR_MAP_ID"
      zoomControl={true}
      gestureHandling="auto"
      minZoom={3}
      maxZoom={18}
    >
      <PoiMarkers
        pois={markerLocations}
        observations={observations}
        onMarkerClick={onMarkerClick}
        t={t}
      />
    </GoogleMap>
  );
}

// MyObservations Component
export default function MyObservations() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isLgDown = useMediaQuery(theme.breakpoints.down("lg"));
  const { toast, setToast, handleToastClose } = useApiErrorHandler();
  const { mutateAsync: deleteData } = useDeleteData("/api/v1/observations", [
    "observations",
  ]);
  const [pageTokens, setPageTokens] = useState([null]);
  const [pages, setPages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [selectedObservation, setSelectedObservation] = useState(null);
  const [loadingStates, setLoadingStates] = useState({});
  const [selectedTab, setSelectedTab] = useState(0);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [initialTotalCount, setInitialTotalCount] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);

  const currentPageToken = pageTokens[currentPageIndex];
  const queryUrl = currentPageToken
    ? `/api/v1/users/my-observations?cursor=${currentPageToken}`
    : `/api/v1/users/my-observations`;

  const {
    data: myObservations,
    isLoading,
    isFetching,
    error,
    refetch,
    isPlaceholderData,
    isError,
  } = useAuthenticatedQuery(["myobservations", currentPageToken], queryUrl, {
    staleTime: 0,
    refetchOnMount: "always",
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (isPlaceholderData) return;
    if (isError) return;
    if (!myObservations || !Array.isArray(myObservations.records)) return;

    const sanitized = myObservations.records.filter((r) => r && r.id);

    if (currentPageToken == null) {
      setPages([sanitized]);
      setCurrentPageIndex(0);
      setInitialTotalCount(
        typeof myObservations.total_count === "number"
          ? myObservations.total_count
          : Number(myObservations.total_count) || 0
      );
    } else {
      setPages((prev) => {
        const existingIds = new Set(
          prev.flat().filter(Boolean).map((r) => r.id).filter(Boolean)
        );
        const filteredRecords = sanitized.filter((r) => !existingIds.has(r.id));
        const newPages = [...prev];
        while (newPages.length <= currentPageIndex) {
          newPages.push([]);
        }
        newPages[currentPageIndex] = filteredRecords;
        return newPages;
      });
    }

    setNextPageToken(myObservations.next_page ?? null);

    if (myObservations.next_page) {
      setPageTokens((prev) => {
        if (prev.includes(myObservations.next_page)) return prev;
        return [...prev, myObservations.next_page];
      });
    }
  }, [
    myObservations,
    currentPageToken,
    currentPageIndex,
    isPlaceholderData,
    isError,
  ]);

  const records = useMemo(() => {
    const flat = pages
      .slice(0, currentPageIndex + 1)
      .flat()
      .filter((obs) => obs && obs.id);
    const seen = new Set();
    return flat.filter((obs) => {
      if (seen.has(obs.id)) return false;
      seen.add(obs.id);
      return true;
    });
  }, [pages, currentPageIndex]);

  const hasPartialResults =
    initialTotalCount != null &&
    initialTotalCount > 0 &&
    records.length < initialTotalCount;

  const { getDetailledData } = useGetDetailledObservation();

  const fetchObservationDetails = async (id) => {
    setLoadingStates((prev) => ({ ...prev, [id]: true }));
    try {
      const data = await getDetailledData(id);
      setSelectedObservation(data);
    } catch (error) {
      // Grab whichever message the API returned
      const apiMsg = error.response?.data?.message;

      // Your generic translation, then the API text
      const fullMessage = apiMsg
        ? `${t("myObservations.errorLoadDetails")}: ${apiMsg}`
        : t("myObservations.errorLoadDetails");

      setToast({
        open: true,
        severity: "error",
        message: fullMessage,
      });
    } finally {
      setLoadingStates((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleDeleteClick = (id) => {
    setItemToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      setLoadingStates((prev) => ({ ...prev, [itemToDelete]: true }));
      try {
        await deleteData(itemToDelete);
        setToast({
          open: true,
          severity: "success",
          message: t("myObservations.deleteSuccess"),
        });
        setPageTokens([null]);
        setPages([]);
        setCurrentPageIndex(0);
        setInitialTotalCount(null);
        setNextPageToken(null);
        refetch();
      } catch (err) {
        const apiMsg = err.response?.data?.message;
        const fullMsg = apiMsg
          ? `${t("myObservations.deleteError")}: ${apiMsg}`
          : t("myObservations.deleteError");

        setToast({
          open: true,
          severity: "error",
          message: fullMsg,
        });
      } finally {
        setItemToDelete(null);
        setDeleteModalOpen(false);
        setLoadingStates((prev) => ({ ...prev, [itemToDelete]: false }));
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleNextPage = () => {
    if (!nextPageToken || isFetching) return;

    const slotEmpty =
      !pages[currentPageIndex] || pages[currentPageIndex]?.length === 0;
    if (currentPageIndex > 0 && slotEmpty && isError) {
      refetch();
      return;
    }

    const nextIdx = currentPageIndex + 1;
    if (nextIdx >= pageTokens.length) return;

    setCurrentPageIndex(nextIdx);
  };

  const handleOpenMapDialog = () => {
    setMapDialogOpen(true);
  };

  const handleCloseMapDialog = () => {
    setMapDialogOpen(false);
  };

  const tabs = useMemo(() => {
    if (!records.length) return [];
    const allTab = [t("myObservations.allTab"), records];
    const statusMap = new Map();
    records.forEach((obs) => {
      let status = t("myObservations.unknownStatus").toLowerCase();
      if (obs.history_steps && obs.history_steps.length > 0) {
        status =
          obs.history_steps[obs.history_steps.length - 1].status.toLowerCase();
      }
      if (!statusMap.has(status)) {
        statusMap.set(status, []);
      }
      statusMap.get(status).push(obs);
    });
    const statusTabsArray = Array.from(statusMap.entries());
    statusTabsArray.sort((a, b) => a[0].localeCompare(b[0]));
    return [allTab, ...statusTabsArray];
  }, [records, t]);

  const selectedObservations = useMemo(() => {
    return tabs[selectedTab]?.[1] || [];
  }, [tabs, selectedTab]);

  const markerLocations = useMemo(() => {
    return selectedObservations
      .filter(
        (obs) =>
          obs.id &&
          obs.site &&
          Number.isFinite(obs.site.latitude) &&
          Number.isFinite(obs.site.longitude)
      )
      .map((obs) => ({
        key: obs.id,
        location: {
          lat: obs.site.latitude,
          lng: obs.site.longitude,
        },
      }));
  }, [selectedObservations]);

  const hasMoreObservations = Boolean(nextPageToken);

  // Loading State
  if (isLoading && !records.length) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
        borderRadius={2}
      >
        <CircularProgress
          size={48}
          aria-label={t("myObservations.loadingObservationsAria")}
        />
        <Typography variant="body1" ml={2} color="text.secondary">
          {t("myObservations.loadingObservations")}
        </Typography>
      </Box>
    );
  }

  // Error State (no cached rows)
  if (error && !records.length && !isFetching) {
    return (
      <Box p={3} borderRadius={2} textAlign="center">
        <Typography variant="h6" color={theme.palette.error.main}>
          {t("myObservations.errorTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("myObservations.errorMessage")}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => refetch()}
          sx={{ mt: 2 }}
        >
          {t("myObservations.retryButton")}
        </Button>
      </Box>
    );
  }

  if (!records.length && error && isFetching) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
        borderRadius={2}
      >
        <CircularProgress
          size={48}
          aria-label={t("myObservations.loadingObservationsAria")}
        />
        <Typography variant="body1" ml={2} color="text.secondary">
          {t("myObservations.loading")}
        </Typography>
      </Box>
    );
  }

  // Empty State
  if (!records.length && !error) {
    return (
      <Box p={3} borderRadius={2} textAlign="center">
        <Typography variant="h6" color="text.primary">
          {t("myObservations.noObservationsTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("myObservations.noObservationsMessage")}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: theme.palette.background.default,
        }}
      >
        {/* Compact Enterprise Header */}
        <PageHeader
          icon="📋"
          title={t("myObservations.title") || "My Observations"}
          subtitle={
            t("myObservations.subtitle") ||
            "Manage and track your observation reports"
          }
          stats={[
            {
              label: `${records.length}/${
                initialTotalCount ??
                myObservations?.total_count ??
                records.length
              }`,
            },
          ]}
          actions={[
            {
              label: t("myObservations.viewMapButton") || "View on Map",
              icon: <MapIcon />,
              onClick: handleOpenMapDialog,
              ariaLabel: t("myObservations.viewMapAria"),
            },
          ]}
          t={t}
          translationPrefix="myObservations"
          compact={true}
        />

        {hasPartialResults && (
          <Typography
            variant="caption"
            component="p"
            sx={{ px: { xs: 2, sm: 3 }, mb: 2, color: "text.secondary" }}
          >
            {t("myObservations.partialResultsNotice")}
          </Typography>
        )}

        {error && records.length > 0 && (
          <Alert
            severity="error"
            sx={{ mb: 2, mx: { xs: 2, sm: 3 } }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => refetch()}
                sx={{ fontWeight: 600, textTransform: "none" }}
              >
                {t("myObservations.retryButton")}
              </Button>
            }
          >
            {t("myObservations.loadMoreErrorInline")}
          </Alert>
        )}

        {/* Enhanced Tabs Section */}
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            p: 1,
            mb: 3,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[1],
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            aria-label={t("myObservations.statusTabsAria")}
            variant={isLgDown ? "standard" : "scrollable"}
            scrollButtons={isLgDown ? false : "auto"}
            allowScrollButtonsMobile={!isLgDown}
            sx={{
              "& .MuiTabs-flexContainer": {
                gap: 1,
                flexWrap: isLgDown ? "wrap" : "nowrap",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                minHeight: 48,
                borderRadius: 1.5,
                margin: "4px 0",
                transition: "all 0.2s ease",
                justifyContent: "center",
                maxWidth: isLgDown ? "none" : undefined,
                minWidth: isLgDown ? "auto" : undefined,
                paddingLeft: isLgDown ? 1.5 : undefined,
                paddingRight: isLgDown ? 1.5 : undefined,
                flex: isLgDown ? "0 1 auto" : "0 0 auto",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
                "&.Mui-selected": {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                },
              },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
                backgroundColor: theme.palette.primary.main,
              },
            }}
          >
            {tabs.map(([tabName, observations], index) => (
              <Tab
                key={tabName}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="inherit">{tabName}</Typography>
                    <Box
                      sx={{
                        bgcolor:
                          selectedTab === index
                            ? theme.palette.primary.main
                            : alpha(theme.palette.text.secondary, 0.1),
                        color:
                          selectedTab === index
                            ? theme.palette.primary.contrastText
                            : theme.palette.text.secondary,
                        borderRadius: "12px",
                        px: 1,
                        py: 0.25,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        minWidth: "20px",
                        textAlign: "center",
                      }}
                    >
                      {observations.length}
                    </Box>
                  </Box>
                }
                {...a11yProps(index)}
              />
            ))}
          </Tabs>
        </Box>

        {tabs.map(([tabName, items], index) => (
          <TabPanel value={selectedTab} index={index} key={tabName}>
            {items.length > 0 ? (
              <Grid container spacing={3}>
                {items.map((obs) => (
                  <Grid
                    size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 3 }}
                    key={obs.id}
                  >
                    <ObservationCard
                      observation={obs}
                      loading={!!loadingStates[obs.id]}
                      onDetailClick={fetchObservationDetails}
                      onDeleteClick={handleDeleteClick}
                      t={t}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 8,
                  px: 3,
                  bgcolor: theme.palette.background.paper,
                  borderRadius: 2,
                  border: `1px dashed ${theme.palette.divider}`,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: 64,
                    color: theme.palette.text.disabled,
                    mb: 2,
                  }}
                >
                  🔍
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                  }}
                >
                  {t("myObservations.noObservationsInStatus") ||
                    `No ${tabName.toLowerCase()} observations`}
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  {t("myObservations.noObservationsInStatusDescription") ||
                    "No observations found for this status"}
                </Typography>
              </Box>
            )}
          </TabPanel>
        ))}

        {selectedTab === 0 && hasMoreObservations && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 4,
              p: 3,
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={handleNextPage}
              disabled={!nextPageToken || isFetching}
              aria-label={t("myObservations.loadMoreAria")}
              startIcon={
                isFetching ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                boxShadow: theme.shadows[2],
                "&:hover": {
                  boxShadow: theme.shadows[4],
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              {isFetching
                ? t("myObservations.loading") || "Loading..."
                : t("myObservations.loadMore") || "Load More Observations"}
            </Button>
          </Box>
        )}
      </Box>

      {selectedObservation && (
        <ObservationDetailsDialog
          observation={selectedObservation}
          onClose={() => setSelectedObservation(null)}
          onActionSuccess={() => {
            setSelectedObservation(null);
            setPageTokens([null]);
            setPages([]);
            setCurrentPageIndex(0);
            setInitialTotalCount(null);
            setNextPageToken(null);
            refetch();
          }}
        />
      )}

      <Dialog
        maxWidth="lg"
        open={mapDialogOpen}
        onClose={handleCloseMapDialog}
        sx={{ "& .MuiDialog-paper": { width: "800px", borderRadius: 2 } }}
      >
        <DialogTitle>
          {t("myObservations.mapTitle")}
          <IconButton
            onClick={handleCloseMapDialog}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <APIProvider
            apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
            libraries={["places"]}
            onLoad={() => setMapsLoaded(true)}
          >
            <ObservationsMap
              mapsLoaded={mapsLoaded}
              observations={selectedObservations}
              markerLocations={markerLocations}
              onClose={handleCloseMapDialog}
              onMarkerClick={fetchObservationDetails}
              t={t}
            />
          </APIProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMapDialog}>
            {t("myObservations.closeButton")}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteModal
        isDeleteModalOpen={deleteModalOpen}
        setDeleteIsModalOpen={setDeleteModalOpen}
        handleDeleteItem={handleConfirmDelete}
        itemType={t("myObservations.itemTypeObservation")}
      />

      <Toast
        open={toast.open}
        onClose={handleToastClose}
        severity={toast.severity}
        message={toast.message}
      />
    </>
  );
}
